import { type ReactElement } from "react"

import { useTranslation } from "react-i18next"

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import { Avatar, Box, Card, Grid } from "@mui/material"
import { Button, type InputChangeEvent, InputText, Typography } from "@synapse-analytics/synapse-ui"
import { type FormikErrors, useFormikContext } from "formik"

import { InfoBlock } from "../../../components/containers/InfoBlock"
import type { CalculatorEquation } from "../../../types/generated/api/CalculatorEquation"
import type { FORMIK_INITIAL_VALUES } from "../Constants"
import type { ConditionCardProps } from "../interfaces"

import styles from "./ConditionCard.module.scss"

export function ConditionCard(props: Readonly<ConditionCardProps>): ReactElement {
  const { equation, name, setEquation, setName, order, isReadMode = true, isLastCard, onReorderList, onDelete } = props

  const { handleBlur, errors, touched } = useFormikContext<typeof FORMIK_INITIAL_VALUES>() ?? {}

  const { t } = useTranslation("calculators")

  return (
    <Card className={styles.root}>
      <Box display={"flex"}>
        {order && (
          <Box className={styles.avatarBlockContainer}>
            {!isReadMode && (
              <Grid item>
                <Button variant="primary" size="small" onClick={() => onReorderList?.("up")} disabled={order === 1}>
                  <ArrowUpwardIcon fontSize="inherit" />
                </Button>
              </Grid>
            )}

            <Avatar sx={{ width: 20, height: 20 }} className={styles.avatar}>
              <Typography variant="label-bold" color="base" variantColor={2}>
                {order}
              </Typography>
            </Avatar>

            {!isReadMode && (
              <Grid item>
                <Button variant="primary" size="small" onClick={() => onReorderList?.("down")} disabled={isLastCard}>
                  <ArrowDownwardIcon fontSize="inherit" />
                </Button>
              </Grid>
            )}
          </Box>
        )}

        <Grid container gap={1.5}>
          <Grid item xs={12}>
            {isReadMode ? (
              <InfoBlock type="default" text={name} textTransform="initial" />
            ) : (
              <InputText
                id={`equations.${(order ?? 1) - 1}.computed_feature_name`}
                value={name}
                placeholder={t("calculatorNamePlaceholder")}
                handleChange={(e: InputChangeEvent) => setName?.(e.target.value as string)}
                error={
                  Boolean(
                    (errors.equations?.[(order ?? 1) - 1] as FormikErrors<CalculatorEquation>)?.computed_feature_name,
                  ) &&
                  touched?.equations?.[(order ?? 1) - 1]?.computed_feature_name &&
                  (errors.equations?.[(order ?? 1) - 1] as FormikErrors<CalculatorEquation>)?.computed_feature_name
                }
                handleBlur={handleBlur}
                fullWidth
                hideDescription
              />
            )}
          </Grid>
          <Grid item xs={12} gap={1} display={"flex"}>
            <InfoBlock type="default" text={t("euqalsInfoBlock")} className={styles.infoBlock} />

            {isReadMode ? (
              <InfoBlock type="default" text={equation} textTransform="initial" />
            ) : (
              <InputText
                value={equation}
                placeholder="1 + 1"
                handleChange={(e: InputChangeEvent) => setEquation?.(e.target.value as string)}
                fullWidth
                hideDescription
              />
            )}
          </Grid>
        </Grid>

        {!isReadMode && onDelete && (
          <Box width={"50px"} alignContent={"center"} justifyItems={"end"}>
            <Button variant="secondary" onClick={() => onDelete()}>
              <DeleteOutlineIcon fontSize="inherit" />
            </Button>
          </Box>
        )}
      </Box>
    </Card>
  )
}
